import React, { useState, useEffect } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import * as graphql from 'generated/graphql';
import RecipientSelectorOption from 'packages/kakapo-ui-kit/components/recipients/RecipientSelectorOption';
import PersonAvatar, {
	PersonAvatarSize,
} from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import { RecipientUserOption } from 'packages/kakapo-ui-services/models/Recipient';
import RecipientService from 'packages/kakapo-ui-services/services/recipients/RecipientsService';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import PlusIconSmall from 'packages/kakapo-ui-kit/assets/icons/PlusIconSmall';
import RelativeContainer from 'packages/kakapo-ui-kit/components/atoms/RelativeContainer';
import CloseIcon from 'packages/kakapo-ui-kit/assets/icons/CloseIcon';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import useImperativeSearch from 'packages/kakapo-ui-application/hooks/useImperativeSearch';

interface IButtonProps {
	small?: boolean;
}

const UserSelectorPlus = styled.div<IButtonProps>`
	width: 38px;
	height: 38px;
	border-radius: 19px;
	border: 1px dashed ${(props) => props.theme.colors.lightBlueDark};
	display: flex;
	align-items: center;
	justify-content: center;

	${({ small }) =>
		small &&
		`
    width: 28px;
		height: 28px;
		border-radius: 14px;
		padding: 7px;
  `}
`;

const UserSelectorButtonContainer = styled.button`
	width: 100%;
	padding: 0;
	background-color: transparent;

	:hover {
		${UserSelectorPlus} {
			border-color: ${(props) => props.theme.colors.slateBlue};
		}

		${ParagraphSmall} {
			color: ${(props) => props.theme.colors.antracite};
		}
	}
`;

export function UserSelectorButton(props: { onClick: () => void; small?: boolean }) {
	const { t } = useTranslation();

	return (
		<UserSelectorButtonContainer type="button" onClick={() => props.onClick()}>
			<Flex alignItems="center">
				<Box mr={3}>
					<UserSelectorPlus small={props.small}>
						<PlusIconSmall color="#6C7680" />
					</UserSelectorPlus>
				</Box>

				<Box>
					<ParagraphSmall color="slateBlue">
						{t('users.collaborators.selector.add.buttonTitle')}
					</ParagraphSmall>
				</Box>
			</Flex>
		</UserSelectorButtonContainer>
	);
}

export const UserSelector = ({
	selectedUsers,
	onSelectUser,
	small,
	excludeViewer,
}: {
	selectedUsers: graphql.User[];
	onSelectUser: (user: graphql.User) => void;
	small?: boolean;
	excludeViewer?: boolean;
}) => {
	const { t } = useTranslation();

	const [isSelectorVisible, setIsSelectorVisible] = useState<boolean>(false);
	const [inputValue, setInputValue] = useState<string>('');
	const [selectedOption, setSelectedOption] = useState<any>(null);
	const [options, setOptions] = useState<RecipientUserOption[]>([]);

	const recipientsFromUser = (user: graphql.User): RecipientUserOption => {
		return {
			label: `${user.firstName} ${user.lastName}`,
			value: user.id!,
			data: user as any,
		};
	};

	const search = useImperativeSearch({
		excludeViewer: excludeViewer ?? true,
	});

	const handleChange = (option: any) => {
		setIsSelectorVisible(false);
		setInputValue('');
		setSelectedOption(null);
		onSelectUser(option.data!);
	};

	useEffect(() => {
		(async () => {
			const results = await search(inputValue, {
				status: graphql.UserStatus.Active,
			});
			setOptions(results.map((user) => recipientsFromUser(user as unknown as graphql.User)));
		})();
	}, [inputValue]);

	return (
		<Box width={[1, 6 / 12]} mb={2}>
			{isSelectorVisible ? (
				<Select
					unspaced
					small
					autoFocus
					onInputChange={setInputValue}
					onBlur={() => {
						setIsSelectorVisible(false);
					}}
					inputValue={inputValue}
					noOptionsMessage={() =>
						inputValue.length > 0
							? t('users.collaborators.selector.noResults')
							: t('users.collaborators.selector.startTypingToSearch')
					}
					options={RecipientService.filterPreviouslyAddedOptions(
						options,
						selectedUsers.map((user) => ({
							userId: user.id!,
							userData: user as any,
						})),
					)}
					components={
						{
							Option: RecipientSelectorOption,
						} as any
					}
					onChange={handleChange}
					value={selectedOption}
				/>
			) : (
				<UserSelectorButton onClick={() => setIsSelectorVisible(true)} small={small} />
			)}
		</Box>
	);
};

const UserCollaboratorRemoveButton = styled.button`
	width: 20px;
	height: 20px;
	border-radius: 10px;
	position: absolute;
	top: -7px;
	right: -7px;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: ${(props) => props.theme.colors.antracite};

	&:hover {
		background-color: ${(props) => props.theme.colors.white};

		svg g g {
			fill: ${(props) => props.theme.colors.antracite};
		}
	}
`;

const UserCollaboratorAvatarElement = styled(Flex)`
	${UserCollaboratorRemoveButton} {
		opacity: 0;
	}

	:hover {
		${UserCollaboratorRemoveButton} {
			opacity: 1;
		}
	}
`;

const UserCollaboratorAvatar = ({
	user,
	isOwner,
	isCurrentUserOwner,
	onUnshare,
}: {
	user: graphql.User;
	isOwner: boolean;
	isCurrentUserOwner: boolean;
	onUnshare: (user: graphql.User) => void;
}) => {
	const { t } = useTranslation();

	return (
		<UserCollaboratorAvatarElement width={[1, 6 / 12, 4 / 12]} mb={2} alignItems="center">
			<Box mr={3}>
				<RelativeContainer>
					<PersonAvatar size={PersonAvatarSize.Medium} person={user} />
					{!isOwner && isCurrentUserOwner && (
						<UserCollaboratorRemoveButton onClick={() => onUnshare(user)}>
							<CloseIcon color="#ffffff" />
						</UserCollaboratorRemoveButton>
					)}
				</RelativeContainer>
			</Box>
			<Box>
				<ParagraphSmall color="slateBlue">
					{user.firstName} {user.lastName}
				</ParagraphSmall>

				<ParagraphSmall color="lightBlueDark">
					{isOwner
						? t('notes.collaborators.role.owner')
						: t('notes.collaborators.role.collaborator')}
				</ParagraphSmall>
			</Box>
		</UserCollaboratorAvatarElement>
	);
};

export default function UserCollaboratorSelector({
	owner,
	collaborators,
	onShare,
	onUnshare,
	disabled,
}: {
	owner: graphql.User;
	collaborators: graphql.User[];
	onShare: (user: graphql.User) => void;
	onUnshare: (user: graphql.User) => void;
	disabled?: boolean;
}) {
	const authenticatedUser = useAuthenticatedUser();
	const isCurrentUserOwner = owner.id === authenticatedUser.id;

	return (
		<Flex flexWrap="wrap">
			{collaborators.map((user) => (
				<UserCollaboratorAvatar
					key={user.id}
					user={user}
					isOwner={user.id === owner.id}
					isCurrentUserOwner={isCurrentUserOwner}
					onUnshare={onUnshare}
				/>
			))}

			{!disabled && (
				<UserSelector
					selectedUsers={[owner, ...collaborators]}
					onSelectUser={(user) => onShare(user)}
				/>
			)}
		</Flex>
	);
}
